import type { IGatsbyImageData } from 'gatsby-plugin-image';
import isChromatic from 'chromatic/isChromatic';
import kebabCase from 'lodash-es/kebabCase';

import Icon from '@klv/atoms/icon';
import Buttons from '@klv/molecules/buttons';
import Image from '@klv/atoms/image';
import VideoPlayButton from '../video-play-button';

import { useModal } from '@klaviyo-mkt/gatsby-components/context/modal';
import { backgroundToTextColor } from '@klv/shared/styles/themes/utils';
import { getBackgroundType } from '@klv/utils/wrapper/functions';
import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/enhancedCard.module.scss';
import { getContainerBackgroundType } from './utils';


import type { TBackgroundColor } from '@klv/utils/wrapper/types';
import type { TIconName } from '@klv/atoms/icon/types';
import type { IEnhancedCard, TECContainerColor } from './types';

export const EnhancedCard = ({
  backgroundColor,
  bodyCopy,
  buttons,
  className = '',
  cardHeaderStyle,
  cardStyle,
  containerColor,
  ctaStyle,
  desktopImage,
  eyebrow,
  headline,
  icon,
  isMobile,
  mediaType,
  mediaPlacement,
  mobileImage,
  onOpenVideo,
  overlay = false,
  textColor,
  textPlacement,
  textWidth,
  videoDuration, 
  videoPoster,
  videoUrl,
  ...props
}: IEnhancedCard) => {
  const chromatic = isChromatic();
  const modalStore = useModal();

  const logoGraphicCard = mediaType && mediaType === 'logo' && ctaStyle === 'graphic' && !bodyCopy && !eyebrow && !headline;
  const showDesktopLogo = (mediaType === 'logo' && !isMobile && desktopImage?.gatsbyImage) || (mediaType === 'logo' && isMobile && !mobileImage?.gatsbyImage && desktopImage?.gatsbyImage);
  const showDesktopImage = ((mediaType === 'image' || cardStyle === 'overlay') && !isMobile && desktopImage?.gatsbyImage) || ((mediaType === 'image' || cardStyle === 'overlay') && isMobile && !mobileImage?.gatsbyImage && desktopImage?.gatsbyImage);
  const textExists = bodyCopy || headline || eyebrow || (buttons && buttons.length > 0);

  const handleMediaClick = () => {
    if (buttons && buttons.length ===  1) {
      window.location.href = buttons[0].url ?? '';
    }
  };
  
  // Overlay card style
  if (cardStyle === 'overlay') {
    return (
      <div
      className={generateClassName([
        className,
        styles.overlayCard,
      ])}
      style={{
        '--ec-card-color': 'transparent',
        '--ec-text-color': textColor,
        '--overlay-text-align': (textPlacement === 'middle' && 'center') || (textPlacement === 'top' && 'space-between')  || 'flex-end',
        '--overlay-text-width': textWidth === 'fullWidth' ? '100%' : '50%',
        '--overlay-overlay': overlay ? 'linear-gradient(0deg, rgba(35, 36, 38, 0.50) 0%, rgba(35, 36, 38, 0.50) 100%)' : 'linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 100%',
      } as React.CSSProperties}
        {...props}
    >
      <div className={styles.overlayCardImg}>
        {showDesktopImage && desktopImage && (
          <Image
            altText={desktopImage?.altText || ''}
            imageData={desktopImage?.gatsbyImage as IGatsbyImageData}
            loading={chromatic ? 'eager' : 'lazy'}
          />
        )}

        {isMobile && mobileImage?.gatsbyImage && (
          <Image
            altText={mobileImage.altText}
            imageData={mobileImage?.gatsbyImage as IGatsbyImageData}
            loading={chromatic ? 'eager' : 'lazy'}
            className=''
          />
        )}
        </div>
        {textExists && (
          <div className={styles.overlayText}>
            <div>
              {eyebrow && <div className={generateClassName(['eyebrow', styles.eyebrow])}>{eyebrow}</div>}
              {headline && <h3 className={generateClassName([styles.headline, cardHeaderStyle === 'inline' ? styles.headlineInline : ''])}>{headline}</h3>}
              {bodyCopy && <div className={generateClassName([styles.bodyCopy, cardHeaderStyle === 'inline' ? styles.bodyCopyInline : '', !buttons ? styles.removeMargin : ''])} dangerouslySetInnerHTML={{ __html: bodyCopy || '' }} />}
            </div>
            {ctaStyle === 'text' && buttons && (
              <div className={generateClassName([styles.overlayButtonContainer])}>
                <Buttons
                  buttons={buttons}
                  buttonColor={textColor === 'white' ? 'light' : 'dark'}
                  variants={['tertiary']}
                />
              </div>
            )}
            {ctaStyle === 'graphic' && buttons && buttons?.length > 0 && (
              <div className={generateClassName([styles.graphicButtonContainer])}>
                <a
                  className={generateClassName([
                    styles.carouselButton,
                    styles[textColor === 'white' ? 'light' : 'dark'],
                  ])}
                  href={buttons[0].url}
                  {...props}
                >
                  <Icon fillColor="currentColor" iconName="ChevronDown" />
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // All other card styles
  return (
    <div
      className={generateClassName([
        className,
        styles.enhancedCard,
        styles[cardStyle as keyof typeof styles],
        logoGraphicCard ? styles.logoGraphicCard : '',
      ])}
      style={{
        '--ec-card-color': containerColor && cardStyle !== 'open' ? `var(--color-${[kebabCase(containerColor) as keyof typeof backgroundToTextColor]})` : 'transparent',
        '--ec-text-color': containerColor && cardStyle !== 'open' ? `var(--txt-to-bkg-${[kebabCase(containerColor) as keyof typeof backgroundToTextColor]})` : 'inherit',
        '--ec-flex-direction': mediaPlacement === 'bottom' ? 'column-reverse' : 'column',
      } as React.CSSProperties}
        {...props}
    >
      {mediaType && mediaType !== 'none' && (
        <>
          <div className={generateClassName([styles.mediaContainer, buttons && buttons.length ===  1 ? styles.mediaContainerWithButton : ''])} onClick={mediaType !== 'video' ? () => handleMediaClick() : undefined}>
            {showDesktopImage && (
              <Image
                altText={desktopImage.altText}
                imageData={desktopImage?.gatsbyImage as IGatsbyImageData}
                loading={chromatic ? 'eager' : 'lazy'}
                className={textExists && cardStyle === 'open' ? styles.imgMargin : ''}
              />
            )}

            {mediaType === 'image' && isMobile && mobileImage?.gatsbyImage && (
              <Image
                altText={mobileImage.altText}
                imageData={mobileImage?.gatsbyImage as IGatsbyImageData}
                loading={chromatic ? 'eager' : 'lazy'}
                className={textExists && cardStyle === 'open' ? styles.imgMargin : ''}
              />
            )}

            {showDesktopLogo  && (
              <div className={generateClassName([
                styles.logoContainer, styles[cardStyle as keyof typeof styles], 
                cardStyle === 'contained' &&  mediaPlacement === 'bottom' ? styles.containedBottom : '',
                logoGraphicCard ? styles.logoGraphicCard : '',
                textExists && cardStyle === 'open' ? styles.logoMargin : '',
              ])}>
                <Image
                  altText={desktopImage.altText}
                  imageData={desktopImage?.gatsbyImage as IGatsbyImageData}
                  loading={chromatic ? 'eager' : 'lazy'}
                />
              </div>
            )}
            {mediaType === 'logo' && isMobile && mobileImage?.gatsbyImage && (
              <div className={generateClassName([ 
                styles.logoContainer, styles[cardStyle as keyof typeof styles], 
                cardStyle === 'contained' &&  mediaPlacement === 'bottom' ? styles.containedBottom : '',
                logoGraphicCard ? styles.logoGraphicCard : '',
                textExists && cardStyle === 'open' ? styles.logoMargin : '',
              ])}>
                <Image
                  altText={mobileImage.altText}
                  imageData={mobileImage?.gatsbyImage as IGatsbyImageData}
                  loading={chromatic ? 'eager' : 'lazy'}
                />
              </div>
            )}

            {mediaType === 'video' && (
              <div 
                className={generateClassName([
                  styles.videoImageContainer,
                  textExists && cardStyle === 'open' ? styles.videoMargin : '',
                ])}
                data-chromatic="ignore"
                onClick={() => onOpenVideo(videoUrl as string, headline as string)}
              >
                <Image
                  altText={videoPoster?.altText}
                  imageData={videoPoster?.gatsbyImage as IGatsbyImageData}
                  loading={chromatic ? 'eager' : 'lazy'}
                />
                <VideoPlayButton videoDuration={videoDuration} />
              </div>
            )}

            {mediaType === 'icon' && (
              <Icon
                className={generateClassName([styles.icon, styles[cardStyle as keyof typeof styles], cardStyle === 'contained' && mediaPlacement === 'bottom' ? styles.containedBottom : '', textExists && cardStyle === 'open' ? styles.iconMargin : ''])}
                data-chromatic="ignore"
                iconName={icon?.iconName as TIconName}
                size={3.0}
              />
            )}
          </div>
        </>
      )}
      {textExists && (
        <div className={generateClassName([styles.textContainer, styles[cardStyle as keyof typeof styles]])}>
          {eyebrow && <div className={generateClassName(['eyebrow', styles.eyebrow])}>{eyebrow}</div>}
          {headline && <h3 className={generateClassName([styles.headline, cardHeaderStyle === 'inline' ? styles.headlineInline : ''])}>{headline}</h3>}
          {bodyCopy && <div className={generateClassName([styles.bodyCopy, cardHeaderStyle === 'inline' ? styles.bodyCopyInline : '', !buttons ? styles.removeMargin : ''])} dangerouslySetInnerHTML={{ __html: bodyCopy || '' }} />}
          
          {ctaStyle === 'text' && buttons && (
            <div>
              <Buttons
                buttons={buttons}
                buttonColor={cardStyle === 'open' ? getContainerBackgroundType(backgroundColor  as TECContainerColor) : getContainerBackgroundType(containerColor as TECContainerColor)}
                variants={['tertiary']}
              />
            </div>
          )}
          {ctaStyle === 'graphic' && buttons && buttons?.length > 0 && (
            <div className={generateClassName([styles.graphicButtonContainer, logoGraphicCard ? styles.logoGraphicCard  : ''])}>
              {buttons[0].url && buttons[0]?.url.includes('?modal=') ? (
                <button
                  className={generateClassName([
                    styles.carouselButton,
                    styles.modalButton,
                    styles[`${containerColor && cardStyle !== 'open' ? getContainerBackgroundType(containerColor as TECContainerColor) : getBackgroundType(backgroundColor as TBackgroundColor)}`],
                  ])}
                  onClick={()=> modalStore.open(buttons[0].url || '')}
                  {...props}
                >
                  <Icon fillColor="currentColor" iconName="PlusOutline" />
                </button>
              ) : (
                  <a
                    className={generateClassName([
                      styles.carouselButton,
                      styles[`${containerColor && cardStyle !== 'open' ? getContainerBackgroundType(containerColor as TECContainerColor) : getBackgroundType(backgroundColor as TBackgroundColor)}`],
                    ])}
                    href={buttons[0].url}
                    {...props}
                  >
                  <Icon fillColor="currentColor" iconName="ChevronDown" />
                </a>
              )}
              </div>
          )}
          </div>
      )}
      </div>
  );
};
